import { useFormBehaviors, useURLParameters } from 'hooks';
import { UseFormBehaviors } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { FC, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { METHOD_PATCH } from 'requests';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { B2B_JANES, AGENCY_JANES } from 'constants/endpoints';
import { IBKJComboBoxAsyncOption } from '@bookjane2/bookjane-design-library/lib/components/BKJComboBoxAsync/BKJComboBoxAsync.types';
import { SessionService, toastService } from 'services';
import { queryRefetchService } from 'services/QueryRefetchService';
import {
  ComboBoxOption,
  VALIDATION_position_id,
  VALIDATION_required,
  monthNumberToName,
  monthToDaysMap,
  DropdownItem,
  prepareEditPayload,
  getPositionByDepartmentSelectOptions,
  GroupedPositionByDepartmentSelectOptionType,
  mapPositionIdsByDepartment,
} from 'modals/JaneDetailsModal/views/EditJaneView/EditJaneView.constants';
import { janeDetailsModalRoutingService } from 'modals/JaneDetailsModal/JaneDetailsModal.constants';
import { useFetchJaneByIdDataContext } from 'modals/JaneDetailsModal/FetchJaneById.data.context';
import { SelectInputPositionByDepartmentContext } from 'modals/JaneDetailsModal/views/EditJaneView/SelectInputPositionByDepartment/SelectInputPositionByDepartment.context';
import { SelectPositionByDepartmentDataType } from 'modals/JaneDetailsModal/views/EditJaneView/SelectInputPositionByDepartment/SelectInputPositionByDepartment.types';
import isUserAgencySelector from 'store/selectors/userSelectors/isUserAgencySelector';
import { useSelector } from 'react-redux';
import i18next from 'i18next';
import { VALIDATION_phoneNumber } from 'components/InviteTeamMembersByCSVModal/views/InviteTeamMemberView/InviteTeamMemberView.constants';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { ChangeEventType } from '@bookjane2/bookjane-design-library/lib/common.types';
import { useAuthRouteDataContext } from 'providers/AuthRouteDataProvider/AuthRouteDataProvider.context';
import { DateTime } from 'luxon';
import {
  isMultiLocationCallOutFeatureEnabledSelector,
  isMultiLocationSchedulingFeatureEnabledSelector,
  isMultiLocationTieringFeatureEnabledSelector,
} from 'store/selectors/featureFlagSelectors';
import { CommunitySeniorityMethodContext } from 'pages/TeamMembers/Internal/CommunitySeniorityMethod.context';
import { removePositionModalRoutingService } from 'pages/Settings/pages/LocationSettings/PositionsAllocation/RemovePositionModal/RemovePositionModal.ui.context';
import { useJanePositionDropdownHandlers } from 'hooks/useJanePositionDropdownHandlers';
import { ShiftParticipationEnum, UsersCommunity } from 'types/graphql';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';

export interface EditJaneApiError {
  data: {
    error_messages: {
      phone?: string[];
    };
  };
}
export interface IEditJaneViewForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isMobile: boolean;
  shiftEmailEnabled: boolean;
  shiftSmsEnabled: boolean;
  locale: string;
  position_id: string[];
  community_id: string;
  location: IBKJComboBoxAsyncOption;
  selectedSecondaryLocationsScheduling: string[];
  selectedSecondaryLocationsCallOut: string[];
  selectedSecondaryPositions: string[];
  employeeStartDate: string;
  employmentStatus: string;
  employeeId: string;
  hourlyRate: number | null;
  birthMonth: string;
  birthDay: string;
  seniorityYears: string;
  seniorityMonths: string;
  seniorityDays: string;
  seniorityHours: string;
}

type SecondaryPositionDropdownItem = DropdownItem & GroupedPositionByDepartmentSelectOptionType;

export interface IEditJaneViewFormActions {
  setSelectMonth: (month: string) => void;
  setSelectDay: (day: string) => void;
  dayOptions: ComboBoxOption[];
  secondaryPositionsDropdownItems: SecondaryPositionDropdownItem[];
  showDeactivateConfirmModal: boolean;
  setShowDeactivateConfirmModal: (showDeactivateConfirmModal: boolean) => void;
  setIsDiscardConfirmationOpen: (isDiscardConfirmationOpen: boolean) => void;
  isDiscardConfirmationOpen: boolean;
  handleRemovedSecondaryPosition: (e: ChangeEventType) => void;
  handleSecondaryPosition: (e: ChangeEventType) => void;
  handlePrimaryPosition: (e: ChangeEventType) => void;
  handlePrimaryLocationChange: (e: ChangeEventType) => void;
  numberOfPositionsDeleted: number;
}
export const EditJaneViewFormContext = createContext(
  {} as UseFormBehaviors<IEditJaneViewForm, EditJaneApiError> & IEditJaneViewFormActions,
);

export const EditJaneViewFormProvider: FC = ({ children }) => {
  const [{ positionId }] = useURLParameters<{ positionId?: string }>();
  const {
    locationFilter: { data: communities },
  } = useAuthRouteDataContext();

  const { data, refetch } = useFetchJaneByIdDataContext();
  const {
    data: positionByDepartmentData,
    refetch: refetchPositionByDepartmentData,
  }: {
    data: SelectPositionByDepartmentDataType;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    refetch: any;
  } = useContext(SelectInputPositionByDepartmentContext);

  const _options = getPositionByDepartmentSelectOptions(positionByDepartmentData);
  const positionIdsByDepartments = mapPositionIdsByDepartment(positionByDepartmentData);
  const secondaryPositionIds = useMemo(
    () =>
      (data?.secondaryPositions || []).map(
        (item: { details: { id: number } }) => `${item.details.id}`,
      ),
    [data?.secondaryPositions],
  );

  const location = getCurrentlySelectedLocation();

  const {
    data: {
      fetchCommunity: {
        organization: { seniorityMethod },
      },
    },
  } = useContext(CommunitySeniorityMethodContext);

  const isMultiLocationTieringEnabled = useSelector(
    isMultiLocationTieringFeatureEnabledSelector(Number(location?.value)),
  );
  const isMultiLocationSchedulingEnabled = useSelector(
    isMultiLocationSchedulingFeatureEnabledSelector(Number(location?.value)),
  );
  const isMultiLocationCallOutEnabled = useSelector(
    isMultiLocationCallOutFeatureEnabledSelector(Number(location?.value)),
  );

  const isUserAgency = useSelector(isUserAgencySelector);
  const { apiRequest } = useAPIActions();
  const monthName = monthNumberToName[Number(data?.birthMonth)];
  const [selectMonth, setSelectMonth] = useState<string>(monthName ? monthName : '');
  const [isDiscardConfirmationOpen, setIsDiscardConfirmationOpen] = useState(false);
  const [selectDay, setSelectDay] = useState<string>(data?.birthDay ? `${data?.birthDay}` : '');
  const [dayOptions, setDayOptions] = useState<ComboBoxOption[]>([]);
  const [showDeactivateConfirmModal, setShowDeactivateConfirmModal] = useState(false);
  const [numberOfPositionsDeleted, setNumberOfPositionsDeleted] = useState(0);

  useEffect(() => {
    setSelectDay('');
  }, [selectMonth]);

  useEffect(() => {
    if (data?.id) {
      queryRefetchService.addDataQuery('TEAM_MEMBERS_SETTINGS_PAGE_TABLE_ROW', data.id, refetch);
    }

    return () => {
      if (data?.id) {
        queryRefetchService.removeDataQuery('TEAM_MEMBERS_SETTINGS_PAGE_TABLE_ROW', data.id);
      }
    };
  }, [data?.id, refetch]);

  const memoizedRefetchData = useMemo(() => {
    return () => {
      if (data?.id) {
        queryRefetchService.refetchDataQueryByKey('TEAM_MEMBERS_SETTINGS_PAGE_TABLE_ROW', data.id);
      }
    };
  }, [data?.id]);

  useMemo(() => {
    if (selectMonth) {
      // Convert month names to the expected structure with unique 'id'
      const options: ComboBoxOption[] = monthToDaysMap[selectMonth].map((day) => ({
        key: `${selectMonth}_${day}`,
        id: `${selectMonth}_${day}`, // Generate a unique 'id' for each day within the selected month
        value: day,
        label: i18next.t(`team_member:${day}`),
      }));
      setDayOptions(options);
    } else {
      setDayOptions([]);
    }
  }, [selectMonth]);

  const convertedEmployeeStartDate = data?.employeeStartDate
    ? DateTime.fromISO(data?.employeeStartDate).toISO()
    : '';
  const handleSave = useCallback(
    async (formValues: IEditJaneViewForm) => {
      const selectedSecondaryPositionsWithoutDepartments =
        formValues.selectedSecondaryPositions.filter(
          (id) => !Object.keys(positionIdsByDepartments).includes(id),
        );
      const editJaneFormValues = {
        ...formValues,
        selectedSecondaryPositions: selectedSecondaryPositionsWithoutDepartments,
      };
      const isSameIds =
        editJaneFormValues.selectedSecondaryPositions.length === data.secondaryPositions.length &&
        editJaneFormValues.selectedSecondaryPositions.every((id: string) =>
          data.secondaryPositions.some(
            (position: { details: { displayId: number } }) =>
              position.details.displayId === Number(id),
          ),
        );
      const payload = prepareEditPayload(
        editJaneFormValues,
        data,
        isUserAgency,
        isSameIds,
        seniorityMethod,
        isMultiLocationTieringEnabled,
        isMultiLocationSchedulingEnabled,
        isMultiLocationCallOutEnabled,
      );
      return await apiRequest(
        {
          __typename: 'LegacyAPI',
          method: METHOD_PATCH,
          url: SessionService.assertUserType('Agency')
            ? AGENCY_JANES(data?.id)
            : B2B_JANES(data?.id),
          body: payload,
        },
        {
          onSuccess: async () => {
            if (refetch) refetch();
            memoizedRefetchData();
            toastService.success(i18next.t('team_member:PROFILE_SUCCESSFULLY_SAVED'));
            positionId
              ? removePositionModalRoutingService.open({ positionId }, 'PositionTeamMembersView')
              : janeDetailsModalRoutingService.goToPrevView();
          },
          onError: handleGenericErrorMessage,
        },
      );
    },
    [
      data,
      isUserAgency,
      seniorityMethod,
      isMultiLocationTieringEnabled,
      isMultiLocationSchedulingEnabled,
      isMultiLocationCallOutEnabled,
      apiRequest,
      positionIdsByDepartments,
      refetch,
      memoizedRefetchData,
      positionId,
    ],
  );

  const community = SessionService.assertUserType('Agency')
    ? data?.agencyCommunity
    : data?.primaryCommunity;

  const locationOptionsTransformer = {
    name: 'location',
    label: community?.name,
    key: community?.id,
    value: community?.id,
    id: community?.id,
    type: community?.typename,
  };
  const form = useFormBehaviors<IEditJaneViewForm, EditJaneApiError>({
    isDirtyCheckEnabled: true,
    initialState: {
      firstName: data?.firstName,
      lastName: data?.lastName,
      email: data?.email,
      phone: data?.phone || '',
      isMobile: data?.isMobile,
      shiftEmailEnabled: data?.shiftEmailEnabled || false,
      shiftSmsEnabled: data?.shiftSmsEnabled || false,
      locale: data?.locale,
      position_id: [`${data?.primaryPosition?.details?.id}`],
      community_id: community?.id || '',
      location: locationOptionsTransformer,
      selectedSecondaryLocationsScheduling: (data?.usersCommunities || [])
        .filter(
          (item: UsersCommunity) =>
            item.shiftParticipation === ShiftParticipationEnum.all ||
            item.shiftParticipation === ShiftParticipationEnum.scheduling,
        )
        .map((item: UsersCommunity) => `${item.community.displayId}`),
      selectedSecondaryLocationsCallOut: (data?.usersCommunities || [])
        .filter(
          (item: UsersCommunity) =>
            item.shiftParticipation === ShiftParticipationEnum.all ||
            item.shiftParticipation === ShiftParticipationEnum.callout,
        )
        .map((item: UsersCommunity) => `${item.community.displayId}`),
      selectedSecondaryPositions: [
        ...secondaryPositionIds,
        ...Object.entries(positionIdsByDepartments)
          .filter(([_, positionIds]) => {
            const filteredPositionIds = positionIds.filter(
              (id) => id !== `${data?.primaryPosition?.details?.id}`,
            );
            return filteredPositionIds.every((id) => secondaryPositionIds.includes(id));
          })
          .map(([department]) => department),
      ],
      employeeStartDate: `${convertedEmployeeStartDate}`,
      employmentStatus: data?.employmentStatus?.name,
      employeeId: data?.employeeNumber || '',
      hourlyRate: data?.hourlyRate,
      birthMonth: `${selectMonth}`,
      birthDay: `${selectDay}`,
      seniorityYears: data?.rankingSeniorityDate?.seniorityYears?.toString() || '0',
      seniorityMonths: data?.rankingSeniorityDate?.seniorityMonths?.toString() || '0',
      seniorityDays: data?.rankingSeniorityDate?.seniorityDays?.toString() || '0',
      seniorityHours: data?.rankingSeniorityDate?.seniorityHours?.toString() || '0',
    },
    onSubmit: handleSave,
    validations: {
      firstName: VALIDATION_required,
      lastName: VALIDATION_required,
      email: VALIDATION_required,
      phone: VALIDATION_phoneNumber,
      position_id: VALIDATION_position_id,
      employmentStatus: VALIDATION_required,
    },
  });

  const handlePrimaryLocationChange = useCallback(
    async (e) => {
      await refetchPositionByDepartmentData({
        filter: isUserAgency
          ? { byAgencyCommunity: [Number(e.target.value?.value)] }
          : { allocatedToCommunities: [Number(e.target.value?.value)] },
      });

      return form.onChange([
        e,
        {
          target: {
            name: 'position_id',
            value: [],
          },
        },
        {
          target: {
            name: 'selectedSecondaryPositions',
            value: [],
          },
        },
        {
          target: {
            name: 'selectedSecondaryLocationsScheduling',
            value: form.values.selectedSecondaryLocationsScheduling.filter(
              (secondaryLocationId) => `${secondaryLocationId}` !== `${e.target.value.value}`,
            ),
          },
        },
        {
          target: {
            name: 'selectedSecondaryLocationsCallOut',
            value: form.values.selectedSecondaryLocationsCallOut.filter(
              (secondaryLocationId) => `${secondaryLocationId}` !== `${e.target.value.value}`,
            ),
          },
        },
      ]);
    },
    [form, refetchPositionByDepartmentData],
  );

  const { handlePrimaryPosition, handleSecondaryPosition, handleRemovedSecondaryPosition } =
    useJanePositionDropdownHandlers({
      onChange: form.onChange,
      positionIdsByDepartments,
      primaryPositionId: form.values.position_id[0],
      secondaryPositionIds: form.values.selectedSecondaryPositions,
      secondaryPositionsFormName: 'selectedSecondaryPositions',
      setShowDeactivateConfirmModal,
      setNumberOfPositionsDeleted,
    });
  const secondaryPositionsDropdownItems = useMemo(
    () =>
      _options &&
      _options
        .filter((item) => `${item.value}` !== `${form.values.position_id[0]}`)
        .filter((item) => {
          // if the department is the only node in this group, filter it out
          const isDepartmentOnlyNodeInGroup =
            item.isGroupNode &&
            positionByDepartmentData[item.groupId]?.filter(
              (positionIdByDepartmentNode) =>
                `${positionIdByDepartmentNode?.value}` !== `${form.values.position_id[0]}`,
            ).length < 1;

          return !isDepartmentOnlyNodeInGroup;
        })
        .map(
          (item) =>
            ({
              id: `${item?.key}`,
              value: item?.value,
              label: item?.label,
              key: `${item?.key}`,
              groupId: `${item?.groupId}`,
              isGroupNode: !!item?.isGroupNode,
            } as SecondaryPositionDropdownItem),
        ),
    [_options, form.values.position_id, positionByDepartmentData],
  );

  const value = useMemo(() => {
    const primaryCommunityId = data?.primaryCommunity?.id;
    const isEditable =
      communities.some((community) => community.id === primaryCommunityId) || isUserAgency;
    return {
      ...form,
      isDisabled:
        !form.values.firstName ||
        !form.values.lastName ||
        !form.values.phone ||
        !form.values.position_id ||
        !form.values.employmentStatus ||
        !isEditable,
      setSelectMonth,
      dayOptions,
      setSelectDay,
      secondaryPositionsDropdownItems,
      showDeactivateConfirmModal,
      setShowDeactivateConfirmModal,
      isDiscardConfirmationOpen,
      setIsDiscardConfirmationOpen,
      handleRemovedSecondaryPosition,
      handleSecondaryPosition,
      handlePrimaryPosition,
      handlePrimaryLocationChange,
      numberOfPositionsDeleted,
    };
  }, [
    data?.primaryCommunity?.id,
    communities,
    isUserAgency,
    form,
    dayOptions,
    secondaryPositionsDropdownItems,
    showDeactivateConfirmModal,
    isDiscardConfirmationOpen,
    handleRemovedSecondaryPosition,
    handleSecondaryPosition,
    handlePrimaryPosition,
    handlePrimaryLocationChange,
    numberOfPositionsDeleted,
  ]);

  return (
    <EditJaneViewFormContext.Provider value={value}>{children}</EditJaneViewFormContext.Provider>
  );
};

export const useEditJaneViewFormContext = () => useContext(EditJaneViewFormContext);
